// Widget de Cloudflare Turnstile. Carga el script una sola vez y renderiza el
// widget en un div contenedor. Soporta dos modos:
//   - size="invisible" (default): widget oculto, verifica sin UI (pagos).
//   - size="normal": widget visible (casilla estándar de Cloudflare) para auth.
//
// Muestra un placeholder de carga mientras el widget se inicializa. Esto es
// importante en el preview del builder (iframe): Turnstile bloquea widgets
// visibles en iframes anidados, así que el placeholder permanece visible y
// el usuario ve dónde iría el widget. En la app publicada el widget real lo
// reemplaza.
import { useEffect, useRef, useState, useId } from "react";
import { base44 } from "@/api/base44Client";
import { Loader2, ShieldCheck } from "lucide-react";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=__turnstileReady__";
let scriptPromise = null;
const readyResolvers = [];
window.__turnstileReady__ = () => { readyResolvers.forEach(r => r()); readyResolvers.length = 0; };

function loadScript() {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    readyResolvers.push(resolve);
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onerror = () => { scriptPromise = null; reject(new Error("turnstile_script_failed")); };
    document.head.appendChild(s);
  });
  return scriptPromise;
}

// Los widgets invisibles NO se montan al cargar la página: esperan a la primera
// interacción real de una persona (clic, tecla o toque). Así rastreadores y
// verificadores (Google brand verification, Lighthouse, Googlebot) no ven el
// reto "Verify you are human" de Cloudflare en la página pública.
const BOT_UA = /bot|crawl|spider|slurp|headless|lighthouse|googleother|inspectiontool|apis-google|adsbot|ptst/i;
function isBot() {
  try { return !!navigator.webdriver || BOT_UA.test(navigator.userAgent || ""); } catch { return false; }
}
function useUserInteracted() {
  const [active, setActive] = useState(() => {
    try { return !isBot() && !!navigator.userActivation?.hasBeenActive; } catch { return false; }
  });
  useEffect(() => {
    if (active || isBot()) return;
    const on = () => setActive(true);
    const evs = ["pointerdown", "keydown", "touchstart"];
    evs.forEach((e) => window.addEventListener(e, on, { once: true, passive: true }));
    return () => evs.forEach((e) => window.removeEventListener(e, on));
  }, [active]);
  return active;
}

export default function TurnstileWidget({ onToken, action, className, theme = "dark", size = "invisible" }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const reactId = useId();
  const [config, setConfig] = useState(null);
  const [rendered, setRendered] = useState(false);
  const [failed, setFailed] = useState(false);
  const visible = size !== "invisible";
  const interacted = useUserInteracted();
  const ready = visible || interacted;

  // Detecta el tema activo (claro/oscuro) para que el widget de Turnstile
  // combine con el resto de la UI. Observa la clase .dark en <html> para
  // reaccionar al toggle de tema y a los skins OS en tiempo real.
  const [isDark, setIsDark] = useState(() => {
    try { return document.documentElement.classList.contains("dark"); }
    catch { return true; }
  });
  useEffect(() => {
    const el = document.documentElement;
    const update = () => setIsDark(el.classList.contains("dark"));
    update();
    const mo = new MutationObserver(update);
    mo.observe(el, { attributes: true, attributeFilter: ["class"] });
    return () => mo.disconnect();
  }, []);
  const renderTheme = isDark ? "dark" : "light";

  // Carga la configuración pública (site key + enabled).
  useEffect(() => {
    let cancelled = false;
    base44.functions.invoke("getTurnstileConfig")
      .then(res => { if (!cancelled) setConfig(res.data || res || {}); })
      .catch(() => { if (!cancelled) setConfig({ siteKey: "", enabled: false }); });
    return () => { cancelled = true; };
  }, []);

  // Renderiza el widget cuando hay config habilitada.
  useEffect(() => {
    if (!config?.enabled || !config?.siteKey) {
      onToken(null); // fail-open: el formulario no bloquea si Turnstile está apagado
      return;
    }
    if (!ready) return; // invisible: espera a la primera interacción humana
    let destroyed = false;
    setRendered(false);
    loadScript().then(() => {
      if (destroyed || !containerRef.current || !window.turnstile) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: config.siteKey,
        theme: renderTheme,
        size,
        action,
        callback: (token) => { setRendered(true); onToken(token); },
        "error-callback": () => onToken(""),
        "expired-callback": () => onToken(""),
        "timeout-callback": () => onToken("")
      });
      // Para widgets visibles: si Turnstile renderiza su iframe, marcar como
      // renderizado. En iframe anidado (preview) el iframe puede inyectarse pero
      // quedar en blanco — el placeholder sigue visible debajo.
      if (visible) {
        setTimeout(() => {
          if (!destroyed && containerRef.current && containerRef.current.querySelector("iframe")) {
            setRendered(true);
          }
        }, 1500);
      }
    }).catch(() => { if (!destroyed) setFailed(true); onToken(""); });
    return () => {
      destroyed = true;
      try {
        if (widgetIdRef.current != null && window.turnstile) window.turnstile.remove(widgetIdRef.current);
      } catch { /* noop */ }
      widgetIdRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.enabled, config?.siteKey, action, renderTheme, size, ready]);

  if (!config?.enabled || !config?.siteKey) return null;

  // Widget invisible: contenedor oculto, sin placeholder.
  if (!visible) {
    return <div ref={containerRef} className={className} data-turnstile-id={reactId} aria-hidden="true" />;
  }

  // Widget visible: contenedor con dimensiones explícitas + placeholder de carga.
  return (
    <div className={className} data-turnstile-id={reactId} aria-hidden="false">
      <div className="relative w-full" style={{ maxWidth: "300px", margin: "0 auto", minHeight: "65px" }}>
        {failed && (
          <div className="absolute inset-0 flex items-center justify-center px-3 text-center border border-destructive/50 bg-ink-surface text-[0.7rem] text-destructive">
            No se pudo cargar la verificación. Revisa tu conexión o desactiva bloqueadores y recarga.
          </div>
        )}
        {!rendered && !failed && (
          <div className="absolute inset-0 flex items-center justify-center gap-2 border border-ink-border bg-ink-surface text-[0.7rem] text-ink-muted">
            <Loader2 className="w-3.5 h-3.5 text-cyan animate-spin" />
            <span>Verificando…</span>
            <ShieldCheck className="w-3.5 h-3.5 text-cyan ml-1" />
          </div>
        )}
        <div ref={containerRef} className="relative z-10 flex justify-center" />
      </div>
    </div>
  );
}